import { createFileRoute, useRouter } from '@tanstack/react-router';
import { recipeDetailQuery, useRecipe } from '@/api/queries/recipes';
import { RecipeEditor } from '@/features/recipes/editor/RecipeEditor';
import type { NewKind } from '@/features/recipes/lib/draft';
import { isRecipeMissing, RecipeLoadError, RecipeNotFound } from '@/features/recipes/RecipeMissing';

/** /recipes/new starts a dish (Ready to sell, makes 1 serving): what "New
 *  dish" in the palette, the dashboard and the recipes list means.
 *  /recipes/new?type=prep starts a prep recipe. /recipes/new?from=12 is the
 *  recipe view's Duplicate: the builder opens on a copy of recipe 12 named
 *  "{name} copy", and nothing is written until Save creates it. */
type Search = { type?: NewKind; from?: number };

export const Route = createFileRoute('/_app/recipes/new')({
  validateSearch: (s: Record<string, unknown>): Search => {
    const from = Number(s.from);
    return {
      type: s.type === 'prep' || s.type === 'dish' ? s.type : undefined,
      from: Number.isInteger(from) && from > 0 ? from : undefined,
    };
  },
  loaderDeps: ({ search }) => ({ from: search.from }),
  loader: async ({ context, deps }) => {
    if (deps.from) await context.queryClient.ensureQueryData(recipeDetailQuery(deps.from));
  },
  errorComponent: ({ error }) => <CopyError error={error} />,
  component: NewRecipe,
});

function CopyError({ error }: { error: unknown }) {
  const router = useRouter();
  return isRecipeMissing(error) ? <RecipeNotFound /> : <RecipeLoadError error={error} onRetry={() => router.invalidate()} />;
}

function NewRecipe() {
  const { type, from } = Route.useSearch();
  const kind: NewKind = type ?? 'dish';
  const source = useRecipe(from ?? 0, { enabled: from !== undefined });
  if (from) {
    if (!source.data) return null;
    // A recipe in Trash is copied like any other: the copy is a new, live
    // recipe of its own.
    return <RecipeEditor key={`copy-${from}`} detail={null} snapshot={null} snapId={null} kind={source.data.is_for_sale ? 'dish' : 'prep'} copyOf={source.data} />;
  }
  // Keyed on the kind, so following a "New prep" link from a new dish
  // starts a fresh form (through the unsaved guard).
  return <RecipeEditor key={kind} detail={null} snapshot={null} snapId={null} kind={kind} />;
}
